import React, { useEffect, useState, useMemo } from 'react';
import api from '../api';
import { useMsal } from '@azure/msal-react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import Pagination from '../components/Pagination';

function ExpenseList({ userRole }) {
  const [expenses, setExpenses] = useState([]);
  const [fullyApprovedMap, setFullyApprovedMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expenseType, setExpenseType] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const userRoles = typeof userRole === 'string' ? userRole.split(',').map(r => r.trim()) : [userRole || 'creator'];
  const hasRole = (role) => userRoles.includes(role);

  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  useEffect(() => {
    const loadExpenses = async () => {
      try {
        setLoading(true);
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ['User.Read'],
          account: accounts[0]
        });

        const res = await api.get('/api/expenses', {
          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (res.data) {
          const list = Array.isArray(res.data) ? res.data : [];
          setExpenses(list);
          // Build derived fully-approved map (Level 3 Approved) for coloring/status display
          if (list.length) {
            try {
              const results = await Promise.all(
                list.map(async (e) => {
                  try {
                    const hRes = await api.get(`/api/approvals/history/by`, { params: { expenseId: e.expense_id } });
                    const history = Array.isArray(hRes.data) ? hRes.data : [];
                    const l3Approved = history.some(h => Number(h.level) === 3 && String(h.status || '').trim() === 'Approved');
                    return [e.expense_id, l3Approved];
                  } catch {
                    return [e.expense_id, false];
                  }
                })
              );
              setFullyApprovedMap(Object.fromEntries(results));
            } catch (e) {
              console.warn('Failed to compute derived approvals for list', e);
              setFullyApprovedMap({});
            }
          }
        }
      } catch (error) {
        console.error('Error fetching expenses:', error);
        setExpenses([]);
      } finally {
        setLoading(false);
      }
    };

    if (accounts && accounts.length > 0) {
      loadExpenses();
    }
  }, [instance, accounts]);

  // Log when expenses change
  useEffect(() => {
    console.log('📝 [ExpenseList] Expenses updated:', {
      count: expenses.length,
      sample: expenses.length > 0 ? expenses[0] : 'No expenses',
      allExpenses: expenses
    });
  }, [expenses]);

  // Request sort function
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (!key) return null;
    if (sortConfig.key === key) {
      return sortConfig.direction === 'desc'
        ? <ChevronDown size={16} />
        : <ChevronUp size={16} />;
    }
    return <ChevronUp size={16} className="opacity-30" />;
  };

  // Apply filters and sorting
  const filteredAndSortedExpenses = useMemo(() => {
    let result = [...expenses];

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(expense =>
        (expense.expense_id && expense.expense_id.toLowerCase().includes(term)) ||
        (expense.expense_name && expense.expense_name.toLowerCase().includes(term))
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(expense =>
        expense.status && expense.status.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Apply expense type filter
    if (expenseType !== 'all') {
      result = result.filter(expense => {
        if (!expense.expense_id) return false;
        const id = expense.expense_id.toUpperCase();
        if (expenseType === 'IT') return id.includes('/IT/');
        if (expenseType === 'Accounts') return id.includes('/ACCOUNT/');
        if (expenseType === 'Admin') return !id.includes('/IT/') && !id.includes('/ACCOUNT/');
        return true;
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        // Handle potential undefined values
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === undefined && bValue === undefined) return 0;
        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [expenses, searchTerm, statusFilter, expenseType, sortConfig]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredAndSortedExpenses.length / itemsPerPage));
  const paginatedExpenses = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedExpenses.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedExpenses, currentPage, itemsPerPage]);

  const goToPage = (page) => {
    const newPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--heading-color, var(--text))' }}>Expense List</h2>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>View and manage all expenses</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'var(--muted)' }} />
          <input
            type="text"
            placeholder="Search by ID or name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 rounded-lg border"
            style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
          />
        </div>
        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="rounded-lg px-4 py-2 border"
            style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
          >
            <option value="all">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
          <select
            value={expenseType}
            onChange={(e) => {
              setExpenseType(e.target.value);
              setCurrentPage(1);
            }}
            className="rounded-lg px-4 py-2 border"
            style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
          >
            <option value="all">All Types</option>
            <option value="IT">IT Expenses</option>
            <option value="Admin">Admin Expenses</option>
            <option value="Accounts">Accounts Expenses</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl shadow-sm overflow-hidden border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--border)' }}>
                <th
                  className="px-6 py-3 text-left text-sm font-medium cursor-pointer"
                  onClick={() => requestSort('expense_id')}
                  style={{ color: 'var(--muted)' }}
                >
                  <div className="flex items-center gap-1">
                    Expense ID
                    {getSortIcon('expense_id')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-sm font-medium cursor-pointer"
                  onClick={() => requestSort('expense_name')}
                  style={{ color: 'var(--muted)' }}
                >
                  <div className="flex items-center gap-1">
                    Expense Name
                    {getSortIcon('expense_name')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-sm font-medium cursor-pointer"
                  onClick={() => requestSort('date')}
                  style={{ color: 'var(--muted)' }}
                >
                  <div className="flex items-center gap-1">
                    Date
                    {getSortIcon('date')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-sm font-medium cursor-pointer"
                  onClick={() => requestSort('status')}
                  style={{ color: 'var(--muted)' }}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {getSortIcon('status')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium" style={{ color: 'var(--muted)' }}>Created By</th>
              </tr>
            </thead>
            <tbody>
              {paginatedExpenses.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm" style={{ color: 'var(--muted)' }}>
                    No expenses found
                  </td>
                </tr>
              ) : (
                paginatedExpenses.map((expense) => (
                  <tr key={expense.id || expense.expense_id} className="border-b hover:opacity-80" style={{ borderColor: 'var(--border)' }}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {(!hasRole('creator') && !hasRole('admin') && (hasRole('approver') || hasRole('payment'))) ? (
                        <span style={{ color: 'var(--text)' }}>{expense.expense_id}</span>
                      ) : (
                        <Link
                          to={`/expense-report/${encodeURIComponent(expense.expense_id)}`}
                          className="hover:underline flex items-center gap-1 font-medium"
                          style={{ color: 'var(--accent)' }}
                        >
                          {expense.expense_id}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm" style={{ color: 'var(--text)' }}>
                      {expense.expense_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text)' }}>
                      {expense.date ? new Date(expense.date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const derivedApproved = !!fullyApprovedMap?.[expense.expense_id];
                        const raw = derivedApproved ? 'Approved' : (expense.status || 'Pending');
                        const status = String(raw);
                        const isPending = status.toLowerCase().startsWith('pending');
                        const isDraft = status.toLowerCase().startsWith('draft') || status === 'Draft';
                        const cls = status === 'Approved'
                          ? 'bg-green-100 text-green-800'
                          : status === 'Rejected'
                            ? 'bg-red-100 text-red-800'
                            : isPending
                              ? 'bg-yellow-100 text-yellow-800'
                              : isDraft
                                ? 'bg-gray-100 text-gray-800'
                                : 'bg-gray-100 text-gray-800';
                        return (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>
                            {status}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text)' }}>
                      {expense.creator_user_id || 'N/A'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredAndSortedExpenses.length > 0 && (
          <div className="px-6 border-t" style={{ borderColor: 'var(--border)' }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
              totalItems={filteredAndSortedExpenses.length}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ExpenseList;
